"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { pollEstimateSearch, startEstimateSearch } from "../_logic/actions/flight-search-actions";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_POLL_BACKOFF,
  SEARCH_POLL_MAX_MS,
  SEARCH_POLL_MS,
  SEARCH_RATE_LIMIT_RETRIES,
  SEARCH_RATE_LIMIT_RETRY_MS,
  SEARCH_TIMEOUT_MS,
} from "../_logic/constants";
import { FARE_GAP, coveringSearch, planSearch, quotesFromSearch, type SearchEntry } from "../_logic/flight-search";
import type { FaresSnapshot } from "../_logic/saved/ui-types";
import type { FareQuote, Workspace } from "../_logic/types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** The flight pipeline's state for the workspace. */
export interface FlightSearchState {
  /** Per-traveler fares for the current plan, keyed by `routeKey(originKey, destination)`. */
  quotes: ReadonlyMap<string, FareQuote>;
  /** The current plan's search, with its results so far. */
  entry: SearchEntry | null;
  status: "idle" | "searching" | "complete" | "error";
  progress: number;
  checkedAt: Date | null;
  /** The current plan's signature, null until there is something to search. */
  signature: string | null;
  /** Searches the current plan again. The pipeline still answers from its 24h cache. */
  refresh: () => void;
  /**
   * Runs (or reuses) the search for another version of the workspace, e.g. other dates or
   * candidate cities, and resolves once it settles. Its results stay cached for when the
   * planner picks it.
   */
  run: (
    workspace: Workspace,
    onUpdate?: (entry: SearchEntry, quotes: Map<string, FareQuote>) => void
  ) => Promise<{ entry: SearchEntry; quotes: Map<string, FareQuote> } | null>;
}

/**
 * Finished searches by plan signature, kept for the page: the pipeline's fares are cached for
 * 24h anyway, so a search this page already ran is never run again by a remounted estimator.
 */
const finishedSearches = new Map<string, SearchEntry>();

/**
 * Runs the workspace's flights through the existing estimator pipeline (traveler groups with
 * their own cabin, the 24h result cache, detailed flight costs data). One search per plan
 * signature: headcount edits reuse it, other edits start a new one after a short pause, and
 * returning to an earlier plan (undo) reuses its search.
 * @param workspace - The workspace.
 * @param saved - A saved estimate's fares, shown until the plan changes or the planner refreshes.
 * @returns Fares, the current search and its progress, and `run` for exploring alternatives.
 * @throws Never; failures surface as missing fares with the reason.
 */
export function useFlightSearch(
  workspace: Workspace,
  saved?: { fares: FaresSnapshot | null; checkedAt: string | null }
): FlightSearchState {
  // States
  // Starts from every search this page already finished, so reopening an estimate or remounting
  // the estimator never searches a plan again; the saved snapshot fills in the rest.
  const [entries, setEntries] = useState<ReadonlyMap<string, SearchEntry>>(() => {
    const seeded = new Map(finishedSearches);
    if (saved?.fares && !seeded.has(saved.fares.signature)) seeded.set(saved.fares.signature, saved.fares.entry);
    return seeded;
  });
  const [checkedAt, setCheckedAt] = useState<Date | null>(() =>
    saved?.fares && saved.checkedAt ? new Date(saved.checkedAt) : null
  );
  const inflight = useRef(new Map<string, Promise<SearchEntry>>());
  /** Each running poll loop's off switch, by signature. */
  const controllers = useRef(new Map<string, AbortController>());
  /** Callers watching a search as results land, by signature. */
  const listeners = useRef(new Map<string, Set<(entry: SearchEntry) => void>>());

  // Derived
  const planned = useMemo(() => planSearch(workspace), [workspace]);
  const entry = planned ? (entries.get(planned.signature) ?? coveringSearch(planned.signature, entries)) : null;
  const quotes = useMemo(
    () => quotesFromSearch(workspace, entry, planned?.unchecked ?? new Set()),
    [workspace, entry, planned]
  );
  const status: FlightSearchState["status"] = !planned
    ? "idle"
    : !entry || (!entry.error && entry.poll?.status !== "complete" && entry.poll?.status !== "error")
      ? entry?.error
        ? "error"
        : "searching"
      : entry.error || entry.poll?.status === "error"
        ? "error"
        : "complete";

  // Handlers
  const store = useCallback((signature: string, next: SearchEntry) => {
    setEntries((prev) => new Map(prev).set(signature, next));
    if (!next.error && next.poll?.status === "complete") finishedSearches.set(signature, next);
    listeners.current.get(signature)?.forEach((listener) => listener(next));
  }, []);

  const execute = useCallback(
    (signature: string, plan: SearchEntry["plan"], dates: NonNullable<Workspace["dates"]>) => {
      const running = inflight.current.get(signature);
      if (running) return running;
      const controller = new AbortController();
      controllers.current.set(signature, controller);
      const { signal } = controller;
      const task = (async (): Promise<SearchEntry> => {
        let current: SearchEntry = { plan, started: null, poll: null, error: null };
        store(signature, current);
        let started = await startEstimateSearch({ ...plan, dates }).catch(() => null);
        // A search refused for too many searches says so and tries again once the window has
        // moved on, instead of settling every route as "no fares".
        for (
          let retries = 0;
          started && !started.ok && started.reason === "rateLimited" && retries < SEARCH_RATE_LIMIT_RETRIES;
          retries += 1
        ) {
          current = { ...current, error: FARE_GAP.rateLimitedRetrying };
          store(signature, current);
          await wait(SEARCH_RATE_LIMIT_RETRY_MS);
          if (signal.aborted) return current;
          started = await startEstimateSearch({ ...plan, dates }).catch(() => null);
        }
        if (!started?.ok) {
          current = { ...current, error: started?.error ?? FARE_GAP.failed };
          store(signature, current);
          return current;
        }
        current = { ...current, started: started.search, error: null };
        store(signature, current);
        if (!started.search.token) {
          current = { ...current, poll: { status: "complete", progress: 1, airportNames: {}, results: [] } };
          store(signature, current);
          return current;
        }
        const deadline = Date.now() + SEARCH_TIMEOUT_MS;
        let interval = SEARCH_POLL_MS;
        while (Date.now() < deadline) {
          await wait(interval);
          // Unmounted, refreshed or replaced: stop polling and keep nothing.
          if (signal.aborted) return current;
          interval = Math.min(SEARCH_POLL_MAX_MS, Math.round(interval * SEARCH_POLL_BACKOFF));
          const polled = await pollEstimateSearch(started.search.token, false).catch(() => null);
          if (signal.aborted) return current;
          if (!polled?.ok) continue;
          current = { ...current, poll: polled.poll };
          store(signature, current);
          setCheckedAt(new Date());
          if (polled.poll.status !== "searching") {
            // Status and results are read side by side; read once more, with itinerary details, so
            // the last results written just before "complete" are not missed.
            const final = await pollEstimateSearch(started.search.token).catch(() => null);
            if (final?.ok && !signal.aborted) {
              current = { ...current, poll: { ...final.poll, status: polled.poll.status } };
              store(signature, current);
            }
            return current;
          }
        }
        // Timed out: what landed shows, but as a failed search (not cached, not saved), with Refresh.
        current = {
          ...current,
          poll: current.poll ? { ...current.poll, status: "error" } : null,
          error: FARE_GAP.timedOut,
        };
        store(signature, current);
        return current;
      })();
      void task.finally(() => {
        if (controllers.current.get(signature) === controller) controllers.current.delete(signature);
      });
      inflight.current.set(signature, task);
      return task;
    },
    [store]
  );

  const run = useCallback<FlightSearchState["run"]>(
    async (alternative, onUpdate) => {
      const next = planSearch(alternative);
      if (!next || !alternative.dates) return null;
      const listener = onUpdate
        ? (entry: SearchEntry) => onUpdate(entry, quotesFromSearch(alternative, entry, next.unchecked))
        : null;
      if (listener) {
        const set = listeners.current.get(next.signature) ?? new Set();
        set.add(listener);
        listeners.current.set(next.signature, set);
      }
      try {
        const settled = await execute(next.signature, next.plan, alternative.dates);
        const quotes = quotesFromSearch(alternative, settled, next.unchecked);
        onUpdate?.(settled, quotes);
        return { entry: settled, quotes };
      } finally {
        if (listener) listeners.current.get(next.signature)?.delete(listener);
      }
    },
    [execute]
  );

  const refresh = useCallback(() => {
    if (!planned || !workspace.dates) return;
    // The old loop for this plan stops before a new one starts.
    controllers.current.get(planned.signature)?.abort();
    inflight.current.delete(planned.signature);
    void execute(planned.signature, planned.plan, workspace.dates);
  }, [planned, workspace.dates, execute]);

  // Effects
  useEffect(() => {
    if (!planned || !workspace.dates || entries.has(planned.signature)) return;
    // A narrower plan (a city or home city removed) reads the search that already priced it.
    if (coveringSearch(planned.signature, entries)) return;
    const dates = workspace.dates;
    const timer = setTimeout(() => void execute(planned.signature, planned.plan, dates), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `entries` is read for the guard only; a stored entry for this signature means it already ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planned?.signature, execute]);

  // Leaving the estimate stops every poll.
  useEffect(() => {
    const running = controllers.current;
    return () => running.forEach((controller) => controller.abort());
  }, []);

  // One object per change, so the estimator's consumers re-render only when the search moves.
  const progress = entry?.poll?.progress ?? 0;
  const signature = planned?.signature ?? null;
  return useMemo(
    () => ({ quotes, entry, status, progress, checkedAt, signature, refresh, run }),
    [quotes, entry, status, progress, checkedAt, signature, refresh, run]
  );
}
